import React, { useEffect, useRef } from "react";
import "./FestiveThemeOverlay.css";

/**
 * Normalizes theme slug with friendly aliases
 */
export const normalizeTheme = (theme) => {
  if (!theme) return null;
  const t = theme.toLowerCase().trim().replace(/[-\s]/g, "_");
  const aliases = {
    pongal: "sankranti",
    makar_sankranti: "sankranti",
    gudi_padwa: "ugadi",
    eid_ul_fitr: "eid",
    ramnavami: "ram_navami",
    vijayadashami: "dussehra",
    childrens_day: "children_day",
    new_year: "newyear",
    independence: "independence_day",
    republic: "republic_day",
  };
  return aliases[t] || t;
};

const THEME_ACCENTS = {
  youth_day: "#38BDF8",
  sankranti: "#F59E0B",
  ugadi: "#22C55E",
  republic_day: "#FF9933",
  shivaratri: "#8EA8FF",
  science_day: "#22D3EE",
  holi: "#EC4899",
  eid: "#34D399",
  ram_navami: "#FB923C",
  baisakhi: "#FBBF24",
  independence_day: "#FF9933",
  janmashtami: "#22D3EE",
  ganesh_chaturthi: "#F97316",
  teachers_day: "#60A5FA",
  onam: "#F59E0B",
  navratri: "#F43F5E",
  dussehra: "#F97316",
  children_day: "#F472B6",
  diwali: "#F59E0B",
  chhath_puja: "#FB923C",
  christmas: "#34D399",
  buddha_purnima: "#FDE047",
  newyear: "#FBBF24",
};

const SUPPORTED_THEMES = Object.keys(THEME_ACCENTS);

const FLOATER_MOTIFS = {
  youth_day: ["bolt", "star"],
  sankranti: ["wheat", "kite"],
  ugadi: ["flower", "star"],
  republic_day: ["star", "flower"],
  shivaratri: ["crescent", "star"],
  science_day: ["atom", "star"],
  holi: ["flower", "star"],
  eid: ["crescent", "star"],
  ram_navami: ["bow", "star"],
  baisakhi: ["wheat", "star"],
  independence_day: ["star", "flower"],
  janmashtami: ["feather", "flute"],
  ganesh_chaturthi: ["ganesh", "flower"],
  teachers_day: ["book", "pencil"],
  onam: ["flower", "lotus"],
  navratri: ["flower", "star"],
  dussehra: ["bow", "star"],
  children_day: ["balloon", "star"],
  diwali: ["diya", "star"],
  chhath_puja: ["sun", "star"],
  christmas: ["snowflake", "star"],
  buddha_purnima: ["lotus", "flower"],
  newyear: ["firework", "star"],
};

const FLOATER_POSITIONS = [
  { left: "4%", top: "16%", size: 46, duration: 18, delay: 0 },
  { left: "8%", top: "58%", size: 34, duration: 15, delay: 1.2 },
  { left: "15%", top: "84%", size: 40, duration: 20, delay: 0.6 },
  { left: "88%", top: "20%", size: 50, duration: 17, delay: 0.3 },
  { left: "94%", top: "54%", size: 34, duration: 14, delay: 2 },
  { left: "83%", top: "82%", size: 44, duration: 19, delay: 1.4 },
  { left: "30%", top: "10%", size: 26, duration: 16, delay: 0.9 },
  { left: "68%", top: "9%", size: 28, duration: 21, delay: 0.4 },
  { left: "45%", top: "91%", size: 30, duration: 15, delay: 1.8 },
  { left: "58%", top: "88%", size: 24, duration: 18, delay: 0.8 },
  { left: "22%", top: "43%", size: 22, duration: 22, delay: 2.4 },
  { left: "77%", top: "43%", size: 24, duration: 16, delay: 1.1 },
];

const hexToRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const full = value.length === 3
    ? value.split("").map((c) => c + c).join("")
    : value;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Small hand-drawn SVG motifs used across the garlands.
 * Drawn with currentColor so each theme can tint them consistently.
 */
const Motif = ({ name, className = "" }) => {
  const common = {
    className: `festive-motif ${className}`,
    viewBox: "0 0 32 32",
    "aria-hidden": "true",
    focusable: "false",
  };

  switch (name) {
    case "bolt":
      return (
        <svg {...common} fill="currentColor">
          <path d="M18.5 2 6.5 18h6.6l-2.6 12L24 14h-6.9l1.4-12z" />
        </svg>
      );

    case "atom":
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeWidth="1.3">
          <circle cx="16" cy="16" r="2.3" fill="currentColor" stroke="none" />
          <ellipse cx="16" cy="16" rx="12" ry="4.6" />
          <ellipse cx="16" cy="16" rx="12" ry="4.6" transform="rotate(60 16 16)" />
          <ellipse cx="16" cy="16" rx="12" ry="4.6" transform="rotate(120 16 16)" />
        </svg>
      );

    case "bow":
      return (
        <svg
          {...common}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 4c11 4.5 11 19.5 0 24" />
          <path d="M8 4v24" strokeWidth="1.2" opacity="0.7" />
          <path d="M8 16h18" />
          <path d="M21 11l6 5-6 5" />
        </svg>
      );

    case "wheat":
      return (
        <svg {...common} fill="currentColor">
          <path
            d="M16 29V8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
          />
          <ellipse cx="12.2" cy="23" rx="2.1" ry="3.9" transform="rotate(-36 12.2 23)" />
          <ellipse cx="19.8" cy="23" rx="2.1" ry="3.9" transform="rotate(36 19.8 23)" />
          <ellipse cx="12.2" cy="18" rx="2.1" ry="3.9" transform="rotate(-36 12.2 18)" />
          <ellipse cx="19.8" cy="18" rx="2.1" ry="3.9" transform="rotate(36 19.8 18)" />
          <ellipse cx="13" cy="13" rx="1.9" ry="3.5" transform="rotate(-32 13 13)" />
          <ellipse cx="19" cy="13" rx="1.9" ry="3.5" transform="rotate(32 19 13)" />
          <ellipse cx="16" cy="8.5" rx="2" ry="4" />
        </svg>
      );

    case "feather":
      return (
        <svg {...common} fill="none">
          <path d="M16 30c0-7 0-14 0-20" stroke="#0EA5E9" strokeWidth="1.5" strokeLinecap="round" />
          <ellipse cx="16" cy="10" rx="6" ry="8" fill="#0D9488" />
          <ellipse cx="16" cy="10" rx="3.5" ry="5.2" fill="#22D3EE" />
          <ellipse cx="16" cy="10" rx="1.6" ry="2.6" fill="#FDE047" />
          <path
            d="M10.2 5.5c3 2.2 4.2 5.2 4.2 8.5M21.8 5.5c-3 2.2-4.2 5.2-4.2 8.5"
            stroke="#0EA5E9"
            strokeWidth="1.3"
            strokeLinecap="round"
            opacity="0.75"
          />
        </svg>
      );

    case "flute":
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeLinecap="round">
          <path d="M6 25L25 6" strokeWidth="2" />
          <path d="M9 22l4 4" strokeWidth="1.5" opacity="0.7" />
          <circle cx="23.5" cy="7.5" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="19.5" cy="11.5" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="15.5" cy="15.5" r="1" fill="currentColor" stroke="none" />
        </svg>
      );

    case "book":
      return (
        <svg
          {...common}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        >
          <path d="M4 7c4-2 8-2 12 0v19c-4-2-8-2-12 0V7z" />
          <path d="M28 7c-4-2-8-2-12 0v19c4-2 8-2 12 0V7z" />
          <path d="M16 7v19" strokeWidth="1.1" opacity="0.6" />
        </svg>
      );

    case "pencil":
      return (
        <svg {...common} fill="currentColor">
          <path d="M5 27l1.4-5.2L20.6 6.6l4 4L10.4 25.6 5 27z" />
          <path d="M20.6 6.6l4 4 2.1-2.1-4-4z" opacity="0.65" />
          <path d="M5 27l1.4-5.2 4 4L5 27z" opacity="0.85" />
        </svg>
      );

    case "balloon":
      return (
        <svg {...common} fill="none">
          <ellipse cx="16" cy="12" rx="7" ry="8.5" fill="currentColor" />
          <path d="M16 20.6l-1.8 1.9h3.6L16 20.6z" fill="currentColor" />
          <path
            d="M16 22.8c0 3.2 3 3 1.9 6.2"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            opacity="0.7"
          />
          <ellipse cx="13" cy="9" rx="1.9" ry="3" fill="#fff" opacity="0.35" />
        </svg>
      );

    case "star":
      return (
        <svg {...common} fill="currentColor">
          <path d="M16 2.5l3.7 8.3 9 .8-6.8 5.9 2 8.8-7.9-4.7-7.9 4.7 2-8.8-6.8-5.9 9-.8z" />
        </svg>
      );

    case "diya":
      return (
        <svg {...common} fill="none">
          <path
            d="M5 17.5c.6 4.6 5 7.5 11 7.5s10.4-2.9 11-7.5H5z"
            fill="currentColor"
            opacity="0.9"
          />
          <path d="M16 4.5c3.1 3.4 3.1 7 0 10.4-3.1-3.4-3.1-7 0-10.4z" fill="#F59E0B" />
          <path d="M16 8.5c1.3 1.7 1.3 3.4 0 5.1-1.3-1.7-1.3-3.4 0-5.1z" fill="#FEF08A" />
        </svg>
      );

    case "snowflake":
      return (
        <svg
          {...common}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        >
          <path d="M16 3v26M5.5 9.5l21 13M26.5 9.5l-21 13" />
          <path d="M16 7l-3-3M16 7l3-3M16 25l-3 3M16 25l3 3M8 12l-3-1M8 12l-1-3M24 20l3 1M24 20l1 3M8 20l-3 1M8 20l-1 3M24 12l3-1M24 12l1-3" />
        </svg>
      );

    case "kite":
      return (
        <svg {...common} fill="none">
          <path d="M16 3l9 10-9 10-9-10z" fill="currentColor" opacity="0.85" />
          <path
            d="M16 23v6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M12 27l4-2 4 2"
            stroke="currentColor"
            strokeWidth="1.2"
            opacity="0.7"
          />
        </svg>
      );

    case "sun":
      return (
        <svg {...common} fill="none" stroke="currentColor" strokeLinecap="round">
          <circle cx="16" cy="13" r="5.4" fill="currentColor" stroke="none" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
            const rad = (angle * Math.PI) / 180;
            return (
              <line
                key={angle}
                x1={16 + Math.cos(rad) * 8}
                y1={13 + Math.sin(rad) * 8}
                x2={16 + Math.cos(rad) * 11}
                y2={13 + Math.sin(rad) * 11}
                strokeWidth="1.4"
              />
            );
          })}
          <path d="M5.5 25c3.4 1.5 6.7 1.5 10 0M12 28.5c3.2 1.2 6.2 1.2 9.4 0" strokeWidth="1.2" opacity="0.55" />
        </svg>
      );

    case "ganesh":
      return (
        <svg {...common} fill="currentColor">
          <circle cx="16" cy="12.5" r="6" />
          <circle cx="7" cy="12.5" r="3.3" />
          <circle cx="25" cy="12.5" r="3.3" />
          <path d="M16 2.8l2.3 3.2h-4.6z" />
          <path
            d="M16 17c0 3 0 4.6 1.6 5.6 1.5 1 3.2.2 3.2-1.4 0-1.3-1.4-1.9-2.1-1"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>
      );

    case "crescent":
      return (
        <svg {...common} fill="currentColor">
          <path d="M21 5a11 11 0 1 0 0 22 9 9 0 0 1 0-22z" />
          <path d="M24.5 8l.9 2.1 2.1.2-1.6 1.4.5 2.1-1.9-1.1-1.9 1.1.5-2.1-1.6-1.4 2.1-.2z" opacity="0.9" />
        </svg>
      );

    case "lotus":
      return (
        <svg {...common} fill="currentColor">
          <path d="M16 27c-6.5 0-10.5-3.2-11.5-7.2 4.3 0 8.4 2.2 11.5 7.2z" opacity="0.85" />
          <path d="M16 27c6.5 0 10.5-3.2 11.5-7.2-4.3 0-8.4 2.2-11.5 7.2z" opacity="0.6" />
          <path d="M16 27c-3.2-4.4-4.2-8.4-2-12.4 2.2 2.2 3.2 5.8 2 12.4z" opacity="0.8" />
          <path d="M16 27c3.2-4.4 4.2-8.4 2-12.4-2.2 2.2-3.2 5.8-2 12.4z" opacity="0.55" />
          <path d="M16 27c-1.2-5 .8-9.4 0-12.6-1.2 3.4-1 8 0 12.6z" opacity="0.9" />
        </svg>
      );

    case "flower":
      return (
        <svg {...common} fill="currentColor">
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
            <ellipse
              key={angle}
              cx="16"
              cy="8.4"
              rx="2.9"
              ry="6"
              transform={`rotate(${angle} 16 16)`}
              opacity={i % 2 === 0 ? 0.95 : 0.6}
            />
          ))}
          <circle cx="16" cy="16" r="3.1" fill="#FDE047" />
        </svg>
      );

    case "firework":
      return (
        <svg
          {...common}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        >
          <circle cx="16" cy="15" r="1.4" fill="currentColor" stroke="none" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => {
            const rad = (angle * Math.PI) / 180;
            return (
              <g key={angle}>
                <line
                  x1={16 + Math.cos(rad) * 4}
                  y1={15 + Math.sin(rad) * 4}
                  x2={16 + Math.cos(rad) * 11}
                  y2={15 + Math.sin(rad) * 11}
                />
                <circle
                  cx={16 + Math.cos(rad) * 12.5}
                  cy={15 + Math.sin(rad) * 12.5}
                  r="0.9"
                  fill="currentColor"
                  stroke="none"
                />
              </g>
            );
          })}
        </svg>
      );

    default:
      return null;
  }
};

const ChakraRow = () => (
  <div className="festive-items-row">
    {Array.from({ length: 9 }).map((_, i) => (
      <div key={i} className="chakra-wrap" style={{ animationDelay: `${i * 0.18}s` }}>
        <svg viewBox="0 0 24 24" className="ashoka-chakra" width="22" height="22">
          <circle cx="12" cy="12" r="10" stroke="#000080" strokeWidth="1.5" fill="none" />
          <circle cx="12" cy="12" r="2" fill="#000080" />
          {Array.from({ length: 24 }).map((__, s) => (
            <line
              key={s}
              x1="12"
              y1="12"
              x2={12 + 10 * Math.cos((s * 15 * Math.PI) / 180)}
              y2={12 + 10 * Math.sin((s * 15 * Math.PI) / 180)}
              stroke="#000080"
              strokeWidth={s % 2 === 0 ? "0.8" : "0.5"}
            />
          ))}
        </svg>
      </div>
    ))}
  </div>
);

const FestiveThemeOverlay = ({ theme }) => {
  const canvasRef = useRef(null);
  const activeKey = normalizeTheme(theme);
  const isValid = SUPPORTED_THEMES.includes(activeKey);

  useEffect(() => {
    if (!isValid) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animationFrameId;
    let particles = [];
    let width = window.innerWidth;
    let height = window.innerHeight;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const initParticles = () => {
      particles = [];
      if (prefersReducedMotion) return;

      let count = 56;
      if (width < 900) count = 36;
      if (width < 600) count = 24;

      for (let i = 0; i < count; i++) {
        const p = {
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.6,
          vy: Math.random() * 0.8 + 0.3,
          radius: Math.random() * 3.5 + 1.6,
          opacity: Math.random() * 0.5 + 0.45,
          rotation: Math.random() * 360,
          vRot: (Math.random() - 0.5) * 3,
          swing: Math.random() * Math.PI,
          vSwing: Math.random() * 0.02 + 0.01,
          twinkle: Math.random() * Math.PI * 2,
          twinkleSpeed: Math.random() * 0.03 + 0.015,
          type: "circle",
          color: "rgba(255, 255, 255,",
        };

        switch (activeKey) {
          case "christmas":
            p.color = Math.random() > 0.65 ? "rgba(251, 191, 36," : "rgba(255, 255, 255,";
            p.radius = Math.random() * 4 + 1.8;
            p.vy = Math.random() * 0.9 + 0.4;
            p.type = Math.random() > 0.7 ? "star" : "circle";
            break;

          case "diwali":
          case "dussehra":
          case "ram_navami":
            p.vy = -(Math.random() * 0.7 + 0.2);
            p.color = Math.random() > 0.4 ? "rgba(251, 191, 36," : "rgba(245, 158, 11,";
            p.type = "sparkle";
            p.radius = Math.random() * 3 + 1.8;
            break;

          case "republic_day":
          case "independence_day": {
            const tri = ["rgba(255, 153, 51,", "rgba(255, 255, 255,", "rgba(19, 136, 8,"];
            p.color = tri[Math.floor(Math.random() * tri.length)];
            p.type = "petal";
            p.radius = Math.random() * 4.5 + 2.5;
            break;
          }

          case "holi": {
            const holiColors = [
              "rgba(244, 63, 94,",
              "rgba(234, 179, 8,",
              "rgba(56, 189, 248,",
              "rgba(168, 85, 247,",
              "rgba(34, 197, 94,",
            ];
            p.color = holiColors[Math.floor(Math.random() * holiColors.length)];
            p.type = "gulal";
            p.radius = Math.random() * 6 + 3;
            p.opacity = Math.random() * 0.4 + 0.15;
            break;
          }

          case "onam":
          case "ugadi": {
            const petals = ["rgba(251, 191, 36,", "rgba(244, 63, 94,", "rgba(255, 255, 255,"];
            p.color = petals[Math.floor(Math.random() * petals.length)];
            p.type = "petal";
            p.radius = Math.random() * 5 + 2.5;
            p.vy = Math.random() * 0.7 + 0.3;
            break;
          }

          case "sankranti":
          case "baisakhi":
            p.color = Math.random() > 0.5 ? "rgba(245, 158, 11," : "rgba(253, 230, 138,";
            p.type = "circle";
            p.radius = Math.random() * 4.5 + 2;
            break;

          case "newyear":
          case "youth_day": {
            const nyCols = [
              "rgba(251, 191, 36,",
              "rgba(56, 189, 248,",
              "rgba(244, 63, 94,",
              "rgba(168, 85, 247,",
            ];
            p.color = nyCols[Math.floor(Math.random() * nyCols.length)];
            p.type = "confetti";
            p.w = Math.random() * 8 + 4;
            p.h = Math.random() * 5 + 2.5;
            break;
          }

          case "eid":
          case "shivaratri":
            p.color =
              activeKey === "eid" ? "rgba(110, 231, 183," : "rgba(147, 197, 253,";
            p.type = "star";
            p.radius = Math.random() * 3 + 1.4;
            break;

          case "buddha_purnima":
          case "chhath_puja":
            p.color = "rgba(253, 230, 138,";
            p.type = "sparkle";
            p.vy = Math.random() * 0.4 + 0.15;
            break;

          case "science_day":
            p.color = "rgba(56, 189, 248,";
            p.type = "atom";
            p.radius = Math.random() * 3 + 1.6;
            break;

          case "teachers_day":
          case "children_day": {
            const playful = ["rgba(251, 191, 36,", "rgba(59, 130, 246,", "rgba(239, 68, 68,"];
            p.color = playful[Math.floor(Math.random() * playful.length)];
            p.type = "circle";
            p.radius = Math.random() * 3 + 1.5;
            break;
          }

          case "ganesh_chaturthi":
          case "navratri":
            p.color = Math.random() > 0.4 ? "rgba(249, 115, 22," : "rgba(234, 179, 8,";
            p.type = "petal";
            p.radius = Math.random() * 3 + 1.5;
            break;

          default:
            p.color = "rgba(251, 191, 36,";
            break;
        }

        particles.push(p);
      }
    };

    const resizeCanvas = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      initParticles();
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    let tick = 0;
    const render = () => {
      if (document.hidden) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      ctx.clearRect(0, 0, width, height);
      tick++;

      particles.forEach((p) => {
        p.y += p.vy;
        p.x += Math.sin(tick * p.vSwing + p.swing) * 0.4;
        p.rotation += p.vRot;

        if (p.vy > 0 && p.y > height + 15) {
          p.y = -15;
          p.x = Math.random() * width;
        } else if (p.vy < 0 && p.y < -15) {
          p.y = height + 15;
          p.x = Math.random() * width;
        }

        const alpha = Math.max(
          0.08,
          Math.min(1, p.opacity * (0.82 + 0.3 * Math.sin(p.twinkle + tick * p.twinkleSpeed)))
        );

        if (p.type === "confetti") {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.fillStyle = `${p.color} ${alpha})`;
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          ctx.restore();
        } else if (p.type === "petal") {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.beginPath();
          ctx.ellipse(0, 0, p.radius * 1.5, p.radius, 0, 0, Math.PI * 2);
          ctx.fillStyle = `${p.color} ${alpha * 0.75})`;
          ctx.fill();
          ctx.restore();
        } else if (p.type === "sparkle") {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          ctx.fillStyle = `${p.color} ${alpha})`;
          ctx.shadowBlur = 8;
          ctx.shadowColor = `${p.color} 0.85)`;
          ctx.fill();
          ctx.shadowBlur = 0;
        } else if (p.type === "star") {
          ctx.save();
          ctx.strokeStyle = `${p.color} ${alpha})`;
          ctx.lineWidth = 1.2;
          ctx.lineCap = "round";
          ctx.shadowBlur = 6;
          ctx.shadowColor = `${p.color} 0.8)`;
          ctx.beginPath();
          ctx.moveTo(p.x - p.radius * 2, p.y);
          ctx.lineTo(p.x + p.radius * 2, p.y);
          ctx.moveTo(p.x, p.y - p.radius * 2);
          ctx.lineTo(p.x, p.y + p.radius * 2);
          ctx.stroke();
          ctx.restore();
        } else if (p.type === "atom") {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate((p.rotation * Math.PI) / 180);
          ctx.strokeStyle = `${p.color} ${alpha * 0.8})`;
          ctx.lineWidth = 0.9;
          ctx.beginPath();
          ctx.ellipse(0, 0, p.radius * 2.6, p.radius, 0, 0, Math.PI * 2);
          ctx.stroke();
          ctx.rotate(Math.PI / 3);
          ctx.beginPath();
          ctx.ellipse(0, 0, p.radius * 2.6, p.radius, 0, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = `${p.color} ${alpha})`;
          ctx.beginPath();
          ctx.arc(0, 0, p.radius * 0.55, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        } else if (p.type === "gulal") {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius * 2, 0, Math.PI * 2);
          ctx.fillStyle = `${p.color} ${alpha * 0.45})`;
          ctx.shadowBlur = 12;
          ctx.shadowColor = `${p.color} 0.5)`;
          ctx.fill();
          ctx.shadowBlur = 0;
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          ctx.fillStyle = `${p.color} ${alpha})`;
          ctx.fill();
        }
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [activeKey, isValid]);

  if (!isValid) return null;

  const accent = THEME_ACCENTS[activeKey];
  const floaterMotifs = FLOATER_MOTIFS[activeKey] || ["star"];
  const overlayStyle = {
    "--festive-accent": accent,
    "--festive-glow": hexToRgba(accent, 0.3),
    "--festive-glow-soft": hexToRgba(accent, 0.14),
  };

  return (
    <div
      className={`festive-overlay festive-${activeKey}`}
      style={overlayStyle}
      aria-hidden="true"
    >
      <div className="festive-ambience" />

      <div className="festive-floaters">
        {FLOATER_POSITIONS.map((pos, i) => (
          <span
            key={i}
            className="festive-floater"
            style={{
              left: pos.left,
              top: pos.top,
              width: pos.size,
              height: pos.size,
              opacity: 0.18 + (i % 4) * 0.035,
              animationDuration: `${pos.duration}s`,
              animationDelay: `${pos.delay}s`,
            }}
          >
            <Motif name={floaterMotifs[i % floaterMotifs.length]} />
          </span>
        ))}
      </div>

      {/* 1. National Youth Day */}
      {activeKey === "youth_day" && (
        <div className="festive-garland festive-garland-youth">
          <div className="festive-bar youth-energy-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 14 }).map((_, i) => (
              <span key={i} className={`youth-spark spark-pos-${i % 3}`}>
                <Motif name="bolt" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 2. Makar Sankranti / Pongal */}
      {activeKey === "sankranti" && (
        <div className="festive-garland festive-garland-sankranti">
          <div className="festive-bar sankranti-sky-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 12 }).map((_, i) =>
              i % 4 === 3 ? (
                <span
                  key={i}
                  className="sankranti-harvest"
                  style={{ animationDelay: `${(i % 3) * 0.3}s` }}
                >
                  <Motif name="wheat" />
                </span>
              ) : (
                <div
                  key={i}
                  className={`sankranti-kite kite-color-${i % 4}`}
                  style={{ animationDelay: `${(i % 5) * 0.25}s` }}
                >
                  <div className="kite-diamond" />
                  <div className="kite-tail" />
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* 3. Ugadi / Gudi Padwa */}
      {activeKey === "ugadi" && (
        <div className="festive-garland festive-garland-ugadi">
          <div className="toran-string" />
          <div className="festive-items-row">
            {Array.from({ length: 16 }).map((_, i) => (
              <div
                key={i}
                className={`mango-leaf leaf-alt-${i % 2}`}
                style={{ animationDelay: `${(i % 4) * 0.3}s` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* 4. Republic Day */}
      {activeKey === "republic_day" && (
        <div className="festive-garland festive-garland-republic">
          <div className="tricolour-top-ribbon" />
          <ChakraRow />
        </div>
      )}

      {/* 5. Maha Shivaratri */}
      {activeKey === "shivaratri" && (
        <div className="festive-garland festive-garland-shivaratri">
          <div className="festive-bar shiva-night-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 11 }).map((_, i) => (
              <div key={i} className="shiva-pendant">
                <span className="shiva-crescent">
                  <Motif name="crescent" />
                </span>
                <span className="shiva-dot" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 6. National Science Day */}
      {activeKey === "science_day" && (
        <div className="festive-garland festive-garland-science">
          <div className="festive-bar science-cyan-line" />
          <div className="festive-items-row">
            {Array.from({ length: 11 }).map((_, i) => (
              <span
                key={i}
                className="science-atom"
                style={{ animationDelay: `${(i % 4) * -1.6}s` }}
              >
                <Motif name="atom" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 7. Holi */}
      {activeKey === "holi" && (
        <div className="festive-garland festive-garland-holi">
          <div className="festive-bar holi-splash-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 15 }).map((_, i) => (
              <div
                key={i}
                className={`gulal-dot dot-c-${i % 5}`}
                style={{ animationDelay: `${(i % 6) * 0.4}s` }}
              />
            ))}
          </div>
        </div>
      )}

      {/* 8. Eid-ul-Fitr */}
      {activeKey === "eid" && (
        <div className="festive-garland festive-garland-eid">
          <div className="eid-string">
            {Array.from({ length: 11 }).map((_, i) => (
              <div key={i} className={`eid-pendant pendant-len-${i % 3}`}>
                <div className="pendant-wire" />
                {i % 2 === 0 ? (
                  <div className="eid-lantern">
                    <div className="lantern-top" />
                    <div className="lantern-body">
                      <div className="lantern-glow" />
                    </div>
                    <div className="lantern-fringe" />
                  </div>
                ) : (
                  <span className="eid-crescent">
                    <Motif name="crescent" />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 9. Ram Navami */}
      {activeKey === "ram_navami" && (
        <div className="festive-garland festive-garland-ramnavami">
          <div className="festive-bar saffron-toran-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 13 }).map((_, i) => (
              <span key={i} className="ram-bow">
                <Motif name="bow" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 10. Baisakhi */}
      {activeKey === "baisakhi" && (
        <div className="festive-garland festive-garland-baisakhi">
          <div className="festive-bar wheat-garland-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 15 }).map((_, i) => (
              <span
                key={i}
                className="wheat-grain"
                style={{ animationDelay: `${(i % 5) * 0.3}s` }}
              >
                <Motif name="wheat" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 11. Independence Day */}
      {activeKey === "independence_day" && (
        <div className="festive-garland festive-garland-republic">
          <div className="tricolour-top-ribbon" />
          <ChakraRow />
        </div>
      )}

      {/* 12. Janmashtami */}
      {activeKey === "janmashtami" && (
        <div className="festive-garland festive-garland-janmashtami">
          <div className="festive-bar peacock-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 11 }).map((_, i) => (
              <div key={i} className="krishna-motif">
                <Motif name={i % 2 === 0 ? "feather" : "flute"} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 13. Ganesh Chaturthi */}
      {activeKey === "ganesh_chaturthi" && (
        <div className="festive-garland festive-garland-ganesh">
          <div className="festive-bar marigold-toran-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 13 }).map((_, i) =>
              i === 6 ? (
                <span key={i} className="ganesh-silhouette">
                  <Motif name="ganesh" />
                </span>
              ) : (
                <div key={i} className="marigold-flower">
                  <div className="flower-center" />
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* 14. Teachers' Day */}
      {activeKey === "teachers_day" && (
        <div className="festive-garland festive-garland-teachers">
          <div className="festive-bar teachers-chalk-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 13 }).map((_, i) => (
              <span
                key={i}
                className={`teacher-icon ${i % 2 === 0 ? "teacher-book" : "teacher-pencil"}`}
                style={{ animationDelay: `${(i % 4) * 0.35}s` }}
              >
                <Motif name={i % 2 === 0 ? "book" : "pencil"} />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 15. Onam */}
      {activeKey === "onam" && (
        <div className="festive-garland festive-garland-onam">
          <div className="festive-bar pookalam-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 15 }).map((_, i) => (
              <span
                key={i}
                className={`pookalam-petal petal-pos-${i % 3}`}
                style={{ animationDelay: `${(i % 5) * 0.3}s` }}
              >
                <Motif name="flower" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 16. Navratri */}
      {activeKey === "navratri" && (
        <div className="festive-garland festive-garland-navratri">
          <div className="festive-bar garba-tinsel-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="dandiya-pair">
                <div className="dandiya-stick stick-left" />
                <div className="dandiya-stick stick-right" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 17. Dussehra */}
      {activeKey === "dussehra" && (
        <div className="festive-garland festive-garland-dussehra">
          <div className="festive-bar saffron-toran-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 13 }).map((_, i) => (
              <span key={i} className="victory-bow">
                <Motif name="bow" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 18. Children's Day */}
      {activeKey === "children_day" && (
        <div className="festive-garland festive-garland-children">
          <div className="festive-bar children-crayon-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 13 }).map((_, i) => (
              <span
                key={i}
                className={`children-item ${i % 2 === 0 ? "children-balloon" : "children-star"}`}
                style={{ animationDelay: `${(i % 5) * 0.3}s` }}
              >
                <Motif name={i % 2 === 0 ? "balloon" : "star"} />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 19. Diwali */}
      {activeKey === "diwali" && (
        <div className="festive-garland festive-garland-diwali">
          <div className="diwali-string">
            {Array.from({ length: 18 }).map((_, i) => (
              <div key={i} className={`diwali-bulb bulb-pos-${i % 3}`}>
                <div className="bulb-wire" />
                <div className="diwali-lamp">
                  <div className="flame-glow" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 20. Chhath Puja */}
      {activeKey === "chhath_puja" && (
        <div className="festive-garland festive-garland-chhath">
          <div className="festive-bar sun-sunrise-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 11 }).map((_, i) => (
              <span key={i} className="sun-diya">
                <Motif name="sun" />
              </span>
            ))}
          </div>
          <div className="chhath-water" />
        </div>
      )}

      {/* 21. Christmas */}
      {activeKey === "christmas" && (
        <div className="festive-garland festive-garland-christmas">
          <div className="xmas-pine-border" />
          <div className="xmas-lights">
            {Array.from({ length: 16 }).map((_, i) =>
              i % 4 === 0 ? (
                <div key={i} className="xmas-star">
                  <Motif name="star" />
                </div>
              ) : (
                <div
                  key={i}
                  className={`xmas-bauble bauble-color-${i % 4}`}
                  style={{ animationDelay: `${(i % 5) * 0.25}s` }}
                >
                  <div className="bauble-cap" />
                  <div className="bauble-sphere" />
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* 22. Buddha Purnima */}
      {activeKey === "buddha_purnima" && (
        <div className="festive-garland festive-garland-buddha">
          <div className="festive-bar lotus-bar" />
          <div className="festive-items-row">
            {Array.from({ length: 13 }).map((_, i) => (
              <span
                key={i}
                className="buddha-lotus"
                style={{ animationDelay: `${(i % 5) * 0.35}s` }}
              >
                <Motif name="lotus" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* New Year */}
      {activeKey === "newyear" && (
        <div className="festive-garland festive-garland-newyear">
          <div className="festive-bar ny-tinsel-bar" />
          <div className="ny-stars">
            {Array.from({ length: 14 }).map((_, i) =>
              i % 4 === 0 ? (
                <div key={i} className={`ny-firework firework-blink-${i % 3}`}>
                  <Motif name="firework" />
                </div>
              ) : (
                <div key={i} className={`ny-star star-blink-${i % 3}`}>
                  <Motif name="star" />
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* ── AMBIENT PARTICLES CANVAS ── */}
      <canvas ref={canvasRef} className="festive-canvas" />

      {/* ── SUBTLE BOTTOM ACCENT ── */}
      <div className="festive-footer" />
    </div>
  );
};

export default FestiveThemeOverlay;
